import { useEffect, useRef, useCallback } from 'react';
import { TextField } from './inputs';

/**
 * Address autocomplete component using Google Places API.
 * Wraps a TextField and provides address suggestions as the user types.
 * When a suggestion is selected, it calls onPlaceSelect with the parsed address components.
 * 
 * NOTE: This is an uncontrolled input for the address field (uses defaultValue) because
 * Google's autocomplete widget manages the input value directly.
 */
export default function AddressAutocomplete({
    id,
    label,
    defaultValue,
    value,
    error,
    helperText,
    disabled,
    onChange,
    onPlaceSelect,
    ...props
}) {
    const autoCompleteRef = useRef();
    const inputRef = useRef();
    const onPlaceSelectRef = useRef(onPlaceSelect);
    onPlaceSelectRef.current = onPlaceSelect;

    const setInputRef = useCallback((node) => {
        inputRef.current = node;
    }, []);

    useEffect(() => {
        if (!inputRef.current) return;

        if (window.location.origin.includes('localhost')) {
            // Google maps API key not configured to allow localhost for security purposes
            return;
        }

        // Only initialize if Google Maps API is loaded
        if (!window.googleMapsEnabled || !window.google?.maps?.places) {
            return;
        }

        let restrictions = { country: ['ca', 'us'] }

        if (['CA', 'ca', 'Canada'].includes(props.country)) {
            restrictions = { country: 'ca' }
        }
        if (['US', 'us', 'United States'].includes(props.country)) {
            restrictions = { country: 'us' }
        }

        // Initialize autocomplete widget
        autoCompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
            componentRestrictions: restrictions,
            fields: ['address_components'],
            types: ['address'],
        });

        // Add listener for when user selects an autocomplete suggestion
        autoCompleteRef.current.addListener('place_changed', async function () {
            try {
                const place = await autoCompleteRef.current.getPlace();

                let streetNum = '';
                let streetName = '';
                let city = '';
                let subLocality = '';
                let province = '';
                let country = '';
                let countryCode = '';
                let postal = '';

                for (const component of place.address_components) {
                    const { types } = component;

                    if (types.includes('street_number')) {
                        streetNum = component.long_name;
                    }
                    if (types.includes('route')) {
                        streetName = component.long_name;
                    }
                    if (types.includes('locality')) {
                        city = component.long_name;
                    }
                    if (types.includes('sublocality_level_1')) {
                        subLocality = component.long_name;
                    }
                    if (types.includes('administrative_area_level_1')) {
                        province = component.short_name;
                    }
                    if (types.includes('country')) {
                        countryCode = component.short_name;
                        country = countryCode === 'CA' ? 'Canada' : countryCode === 'US' ? 'United States' : countryCode;
                    }
                    if (types.includes('postal_code')) {
                        postal = component.short_name.replace(' ', '');
                    }
                }

                const address = `${streetNum} ${streetName}`.trim();

                if (inputRef.current) {
                    inputRef.current.value = address;
                }

                if (onPlaceSelectRef.current) {
                    onPlaceSelectRef.current({
                        address,
                        city: subLocality || city,
                        province,
                        country,
                        countryCode,
                        postalCode: postal,
                    });
                }
            } catch (error) {
                console.error('Error parsing place:', error);
            }
        });

        return () => {
            if (autoCompleteRef.current) {
                window.google.maps.event.clearInstanceListeners(autoCompleteRef.current);
            }
        };
        // eslint-disable-next-line
    }, []);

    return (
        <TextField
            id={id}
            label={label}
            disabled={disabled}
            defaultValue={defaultValue ?? value}
            error={error}
            helperText={helperText}
            onChange={onChange}
            inputRef={setInputRef}
            autoComplete="off"
            {...props}
        />
    );
}
